import React, {useState, useEffect} from 'react';
import {useParams, useNavigate, Link} from 'react-router-dom';
import axios from 'axios';
import {useCart} from '../../context/CartContext';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import './Produits.css';

const API_URL = import.meta.env.VITE_API_URL;

const Produits = ({userType, isAuthenticated}) => {
    const {category} = useParams();
    const navigate = useNavigate();
    const {addToCart} = useCart();
    const [produits, setProduits] = useState([]);
    const [types, setTypes] = useState([]);
    const [filtreMarque, setFiltreMarque] = useState('');
    const [loading, setLoading] = useState(false);
    const [marquesInCategory, setMarquesInCategory] = useState([]);

    // Catégories statiques avec images et descriptions
    const categories = [
        {
            nom: 'Panneaux Solaires',
            description: 'Nos panneaux photovoltaïques de haute qualité offrent une production d\'énergie optimale grâce à des technologies avancées.',
            image: '/home/solar-panel.jpg',
            slug: 'Panneaux Solaires'
        },
        {
            nom: 'Onduleur',
            description: 'Nos onduleurs performants assurent une conversion efficace de l\'énergie solaire en électricité utilisable.',
            image: '/home/onduleur.png',
            slug: 'Onduleur'
        },
        {
            nom: 'Batteries solaires',
            description: 'Nos solutions de stockage d\'énergie révolutionnaires vous offrent une autonomie maximale et une indépendance totale.',
            image: '/home/batterie.jpg',
            slug: 'Batteries solaires'
        },
        {
            nom: 'Structure',
            description: 'Nos structures de montage robustes et durables sont spécialement conçues pour sécuriser vos installations solaires.',
            image: '/home/structure.jpg',
            slug: 'Structure'
        }
    ];

    useEffect(() => {
        fetchTypes();
    }, []);

    useEffect(() => {
        setFiltreMarque('');
        setMarquesInCategory([]);
        if (category) {
            fetchProduits();
        }
    }, [category, types]); // Reset filters on category change

    useEffect(() => {
        if (category) {
            fetchProduits();
        }
    }, [filtreMarque]); // Re-fetch on brand filter change



    const fetchTypes = async () => {
        try {
            const response = await axios.get(`${API_URL}/types-produit`);
            setTypes(Array.isArray(response.data) ? response.data : (response.data?.data || []));
        } catch (error) {
            console.error('Erreur types:', error);
        }
    };

    const fetchProduits = async () => {
        if (!category || types.length === 0) return;

        setLoading(true);
        try {
            let url = `${API_URL}/produits?`;
            if (filtreMarque) url += `marque=${filtreMarque}&`;

            // Match category with type ID
            const typeObj = types.find(t => {
                const labelLower = t.label.toLowerCase().trim();
                const categoryLower = category.toLowerCase().trim();
                return labelLower === categoryLower ||
                    labelLower === categoryLower + 's' ||
                    labelLower + 's' === categoryLower ||
                    labelLower.includes(categoryLower) ||
                    categoryLower.includes(labelLower);
            });

            if (typeObj) {
                url += `typeProduit=${typeObj._id}`;
            }

            const response = await axios.get(url);
            const fetchedProduits = Array.isArray(response.data) ? response.data : (response.data?.data || []);
            setProduits(fetchedProduits);

            // If no brand filter is active, determine brands for this category
            if (!filtreMarque) {
                const brands = [];
                const seen = new Set();
                fetchedProduits.forEach(p => {
                    if (p.marque && !seen.has(p.marque._id)) {
                        brands.push(p.marque);
                        seen.add(p.marque._id);
                    }
                });
                setMarquesInCategory(brands);
            }
        } catch (error) {
            console.error('Erreur produits:', error);
        } finally {
            setLoading(false);
        }
    };

    const selectionnerCategorie = (slug) => {
        navigate(`/produits/${slug}`);
    };

    const resetFiltres = () => {
        setFiltreMarque('');
    };

    return (
        <div className="produits-container">
            {/* Hero Section */}
            <section className="produits-hero">
                <div className="container">
                    <div className="produits-hero-content">
                        <br />
                        <h1 className="produits-title">Nos Produits</h1>
                        <p className="produits-subtitle">
                            {!category
                                ? "Découvrez notre gamme complète de solutions énergétiques"
                                : `Catégorie : ${category}`
                            }
                        </p>
                        {category && (
                            <Link to="/produits" className="retour-categories-btn">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="19" y1="12" x2="5" y2="12"></line>
                                    <polyline points="12 19 5 12 12 5"></polyline>
                                </svg>
                                Retour aux catégories
                            </Link>
                        )}
                    </div>
                </div>
            </section>

            {/* Vue Catégories */}
            {!category ? (
                <div className="categories-grid">
                    {categories.map((cat, index) => (
                        <div
                            key={index}
                            className="categorie-card"
                            onClick={() => selectionnerCategorie(cat.slug)}
                        >
                            <div className="categorie-image-container">
                                <img
                                    src={cat.image}
                                    alt={cat.nom}
                                    onError={(e) => {e.target.src = 'https://via.placeholder.com/400x300?text=' + encodeURIComponent(cat.nom);}}
                                />
                                <div className="categorie-overlay">
                                    <button className="voir-produits-btn">
                                        Voir les produits
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <line x1="5" y1="12" x2="19" y2="12"></line>
                                            <polyline points="12 5 19 12 12 19"></polyline>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                            <div className="categorie-content">
                                <h3>{cat.nom}</h3>
                                <p>{cat.description}</p>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <>
                    {/* Section Marques Détails */}
                    {marquesInCategory.length > 0 && (
                        <section className="partenaires-section">
                            <div className="partenaires-container">
                                <h2>{marquesInCategory.length > 1 ? `Nos marques de ${category}` : `Marque de ${category}`}</h2>
                                <div className="partenaires-grid">
                                    {marquesInCategory.map(marque => (
                                        <div key={marque._id} className="partenaire-card">
                                            <div className="partenaire-logo">
                                                <img 
                                                    src={marque.logo} 
                                                    alt={marque.name}
                                                    onError={(e) => {e.target.src = 'https://via.placeholder.com/350x200?text=' + encodeURIComponent(marque.name);}}
                                                />
                                            </div>
                                            <div className="partenaire-info">
                                                <h3>{marque.name}</h3>
                                                <p className="partenaire-description">{marque.description}</p>
                                                {marque.website && (
                                                    <a href={marque.website} target="_blank" rel="noopener noreferrer" className="partenaire-website">
                                                        <i className="fas fa-globe"></i> Visiter le site
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </section>
                    )}

                    {/* Section Filtres - Uniquement si moteur de filtrage nécessaire */}
                    {marquesInCategory.length > 1 && (
                        <div className="filtres-section">
                            <div className="filtres-container">
                                <div className="filtre-group">
                                    <label htmlFor="marque-filter">
                                        <i className="fas fa-filter"></i> Filtrer par Marque
                                    </label>
                                    <select
                                        id="marque-filter"
                                        value={filtreMarque}
                                        onChange={(e) => setFiltreMarque(e.target.value)}
                                    >
                                        <option value="">Toutes les marques</option>
                                        {marquesInCategory.map(p => (
                                            <option key={p._id} value={p._id}>{p.name}</option>
                                        ))}
                                    </select>
                                </div>
                                {filtreMarque && (
                                    <button className="reset-btn" onClick={resetFiltres}>Réinitialiser</button>
                                )}
                            </div>
                        </div>
                    )}

                    {loading ? (
                        <LoadingSpinner message="Chargement des produits..." />
                    ) : produits.length === 0 ? (
                        <div className="no-produits">
                            <h3>Aucun produit trouvé dans cette catégorie</h3>
                        </div>
                    ) : (
                        <div className="produits-grid">
                            {produits.map(produit => (
                                <Link
                                    to={`/produit/${produit._id}`}
                                    key={produit._id}
                                    className="produit-card"
                                >
                                    <div className="produit-image-container">
                                        <span className="produit-marque-badge">{produit.marque?.name}</span>
                                        <img
                                            src={`${API_URL.replace('/api', '')}${produit.imageProduit}`}
                                            alt={produit.nomProduit}
                                            onError={(e) => {e.target.src = 'https://via.placeholder.com/400x300?text=Indisponible';}}
                                        />
                                        <div className="produit-overlay">
                                            <span className="voir-details-btn">Voir détails</span>
                                        </div>
                                    </div>
                                    <div className="produit-content">
                                        <h3>{produit.nomProduit}</h3>
                                        <p className="produit-description-preview">
                                            {produit.description.substring(0, 80)}...
                                        </p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Produits;
